import React, { useState } from 'react';
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';

// Constants
import { ROUTES } from '../constants/routes.constants';

// Context
import { useAuth } from '../context/AuthContext';

// Components
import Navbar from '../components/layout/Navbar';
import DashboardLayout from '../components/layout/DashboardLayout';
import AuthPage from '../components/auth/AuthPage';
import GoogleCallback from '../components/auth/GoogleCallback';
import AuthError from '../components/auth/AuthError';
import UserRegistration from '../components/user/UserRegistration';
import AgencyRegistration from '../components/agency/AgencyRegistration';

// Pages
import HomePage from '../pages/HomePage';
import VehiclesPage from '../pages/VehiclesPage';
import AdminDashboard from '../pages/admin/AdminDashboard';
import PendingAgencies from '../pages/admin/PendingAgencies';
import AuditLogsPage from '../pages/admin/AuditLogsPage';

// Admin Vehicle Management Pages
import VehicleSettings from '../pages/admin/vehicles/VehicleSettings';
import AddVehicle from '../pages/admin/vehicles/AddVehicle';
import EditVehicle from '../pages/admin/vehicles/EditVehicle';
import VehicleReviewPage from '../pages/admin/vehicles/VehicleReviewPage';
import VehicleList from '../pages/admin/vehicles/VehicleList';
import BookingsList from '../pages/admin/BookingsList';
import UsersManagementPage from '../pages/admin/UsersManagementPage';
import AgenciesList from '../pages/admin/AgenciesList';
import AgenciesListNew from '../pages/admin/AgenciesListNew';
import AgencyDetails from '../pages/admin/AgencyDetails';
import BookingPage from '../pages/booking/BookingPage';
import AgencyBookings from '../components/agency/AgencyBookings';

/**
 * Layout wrapper pour les pages avec navbar
 */
export const Layout = ({ children }) => {
  const location = useLocation();
  const { isAuthenticated, user, logout } = useAuth();
  const [showAuth, setShowAuth] = useState(false);

  // Ne pas afficher la navbar sur certaines pages
  // Masquer la navbar sur les pages dashboard car DashboardLayout inclut déjà une navbar
  // Spécifiquement masquer sur toutes les routes /admin
  const isAdminPage = location.pathname.startsWith('/admin');
  const isDashboardPage = location.pathname.startsWith('/vendor/') ||
                          location.pathname.startsWith('/user/dashboard');
  
  const hideNavbar = [
    ROUTES.AUTH_CALLBACK, 
    ROUTES.AUTH_ERROR
  ].includes(location.pathname) || isAdminPage || isDashboardPage;

  const handleAuthSuccess = () => {
    setShowAuth(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 font-sans text-gray-900">
      {!hideNavbar && (
        <Navbar 
          isLoggedIn={isAuthenticated}
          user={user}
          onLogout={logout}
        />
      )}

      {/* Auth Modal */}
      {showAuth && (
        <AuthPage 
          onClose={() => setShowAuth(false)}
          onSuccess={handleAuthSuccess}
        />
      )}

      {children}
    </div>
  );
};

/**
 * Route wrapper pour vérifier l'authentification, rôles et permissions
 */
export const PrivateRoute = ({
  children,
  requiredRole,
  requiredRoles,
  requiredPermission,
  requiredAnyPermission,
}) => {
  const { isAuthenticated, user, hasPermission, hasAnyPermission, isLoading } = useAuth();

  if (isLoading) {
    return null;
  }

  if (!isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} />;
  }

  const rolesToCheck = requiredRoles?.length
    ? requiredRoles
    : requiredRole
      ? [requiredRole]
      : null;

  if (rolesToCheck) {
    // Compat : "admin" accepte aussi super_admin et staff (panel)
    const expanded = new Set(rolesToCheck);
    if (rolesToCheck.includes('admin')) {
      expanded.add('super_admin');
      expanded.add('staff');
    }
    if (!expanded.has(user?.role)) {
      if (user?.role === 'vendor') {
        return <Navigate to={ROUTES.VENDOR_DASHBOARD} />;
      }
      if (user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'staff') {
        return <Navigate to={ROUTES.ADMIN_DASHBOARD} />;
      }
      return <Navigate to={ROUTES.HOME} />;
    }
  }

  if (requiredPermission && !hasPermission(requiredPermission)) {
    return <Navigate to={ROUTES.ADMIN_DASHBOARD} />;
  }

  if (
    Array.isArray(requiredAnyPermission) &&
    requiredAnyPermission.length > 0 &&
    !hasAnyPermission(...requiredAnyPermission)
  ) {
    return <Navigate to={ROUTES.ADMIN_DASHBOARD} />;
  }

  return children;
};

const LegacyVehicleReviewRedirect = () => {
  const { id } = useParams();
  return <Navigate to={ROUTES.ADMIN_VEHICLES_REVIEW_BY_ID(id)} replace />;
};

const RedirectVehicleSettingsTab = ({ tab }) => (
  <Navigate to={`${ROUTES.ADMIN_VEHICLES_SETTINGS}?tab=${tab}`} replace />
);

/**
 * Composant principal des routes de l'application
 */
export const AppRoutes = () => {
  const location = useLocation();
  
  // Vérifier si on est sur une page de callback Google
  const isGoogleCallback = location.pathname === ROUTES.AUTH_CALLBACK || location.search.includes('token=');
  const isAuthError = location.pathname === ROUTES.AUTH_ERROR;

  if (isGoogleCallback) {
    return <GoogleCallback />;
  }

  if (isAuthError) {
    return <AuthError />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path={ROUTES.HOME} element={
        <Layout>
          <HomePage />
        </Layout>
      } />
      
      <Route path={ROUTES.VEHICLES} element={
        <Layout>
          <VehiclesPage />
        </Layout>
      } />

      <Route path={ROUTES.REGISTER} element={
        <Layout>
          <UserRegistration />
        </Layout>
      } />

      <Route path={ROUTES.BECOME_PARTNER} element={
        <Layout>
          <AgencyRegistration />
        </Layout>
      } />

      <Route path={ROUTES.LOGIN} element={
        <Layout>
          <div className="min-h-screen flex items-center justify-center p-4">
            <AuthPage />
          </div>
        </Layout>
      } />

      {/* Auth Routes */}
      <Route path={ROUTES.AUTH_CALLBACK} element={<GoogleCallback />} />
      <Route path={ROUTES.AUTH_ERROR} element={<AuthError />} />

      {/* Booking Route */}
      <Route path={ROUTES.BOOKING} element={
        <Layout>
          <BookingPage />
        </Layout>
      } />

      {/* Admin Routes */}
      <Route 
        path="/admin/dashboard" 
        element={
          <PrivateRoute requiredRole="admin">
            <DashboardLayout>
              <AdminDashboard />
            </DashboardLayout>
          </PrivateRoute>
        } 
      />
      <Route 
        path="/admin/pending-agencies" 
        element={
          <PrivateRoute requiredRole="admin">
            <DashboardLayout>
              <PendingAgencies />
            </DashboardLayout>
          </PrivateRoute>
        } 
      />
      <Route 
        path={ROUTES.ADMIN_AUDIT_LOGS} 
        element={
          <PrivateRoute requiredRole="admin">
            <DashboardLayout>
              <AuditLogsPage />
            </DashboardLayout>
          </PrivateRoute>
        } 
      />

      <Route 
        path={ROUTES.ADMIN_BOOKINGS} 
        element={
          <PrivateRoute requiredRole="admin">
            <BookingsList />
          </PrivateRoute>
        } 
      />

      <Route 
        path={ROUTES.ADMIN_USERS} 
        element={
          <PrivateRoute requiredRole="admin" requiredPermission="users:read">
            <UsersManagementPage />
          </PrivateRoute>
        } 
      />

      <Route 
        path={ROUTES.ADMIN_AGENCIES} 
        element={
          <PrivateRoute requiredRole="admin">
            <AgenciesListNew />
          </PrivateRoute>
        } 
      />

      <Route 
        path={ROUTES.ADMIN_AGENCY_DETAILS} 
        element={
          <PrivateRoute requiredRole="admin">
            <AgencyDetails />
          </PrivateRoute>
        } 
      />
      
      {/* Admin Vehicle Management Routes */}
      <Route
        path={ROUTES.ADMIN_VEHICLES_SETTINGS}
        element={
          <PrivateRoute requiredRole="admin">
            <VehicleSettings />
          </PrivateRoute>
        }
      />
      <Route path="/admin/vehicles/features" element={<RedirectVehicleSettingsTab tab="features" />} />
      <Route path="/admin/vehicles/types" element={<RedirectVehicleSettingsTab tab="types" />} />
      <Route path="/admin/vehicles/locations" element={<RedirectVehicleSettingsTab tab="locations" />} />
      <Route path="/admin/vehicles/makes" element={<RedirectVehicleSettingsTab tab="makes" />} />
      <Route path="/admin/vehicles/models" element={<RedirectVehicleSettingsTab tab="models" />} />
      <Route path="/admin/vehicles/odometer" element={<RedirectVehicleSettingsTab tab="odometer" />} />
      <Route path="/admin/vehicles/fuel-types" element={<RedirectVehicleSettingsTab tab="fuel-types" />} />
      <Route path="/admin/vehicles/payout-methods" element={<RedirectVehicleSettingsTab tab="payout-methods" />} />
      <Route path="/admin/vehicles/rules" element={<RedirectVehicleSettingsTab tab="rules" />} />
      <Route path="/admin/vehicles/cancellation-policies" element={<RedirectVehicleSettingsTab tab="cancellation-policies" />} />
      <Route path="/admin/vehicles/cancellation-reasons" element={<RedirectVehicleSettingsTab tab="cancellation-reasons" />} />
      <Route path={ROUTES.ADMIN_REJECTION_REASONS} element={<RedirectVehicleSettingsTab tab="rejection-reasons" />} />
      <Route 
        path="/admin/vehicles/add" 
        element={
          <PrivateRoute requiredRole="admin">
            <AddVehicle />
          </PrivateRoute>
        } 
      />
      <Route 
        path={ROUTES.ADMIN_VEHICLES_EDIT} 
        element={
          <PrivateRoute requiredRole="admin">
            <EditVehicle />
          </PrivateRoute>
        } 
      />
      <Route 
        path={ROUTES.ADMIN_VEHICLES_REVIEW} 
        element={
          <PrivateRoute requiredRole="admin">
            <VehicleReviewPage />
          </PrivateRoute>
        } 
      />
      <Route
        path="/admin/vehicles/:id/review"
        element={
          <PrivateRoute requiredRole="admin">
            <LegacyVehicleReviewRedirect />
          </PrivateRoute>
        }
      />
      <Route 
        path="/admin/vehicles/list" 
        element={
          <PrivateRoute requiredRole="admin">
            <VehicleList />
          </PrivateRoute>
        } 
      />

      {/* Vendor Routes */}
      <Route 
        path={ROUTES.VENDOR_DASHBOARD} 
        element={
          <PrivateRoute requiredRole="vendor">
            <DashboardLayout>
              <div className="p-6">
                <h1 className="text-2xl font-bold">Tableau de bord Vendeur</h1>
                <p className="text-gray-600">Bienvenue sur votre tableau de bord</p>
              </div>
            </DashboardLayout>
          </PrivateRoute>
        } 
      />
      <Route 
        path={ROUTES.VENDOR_VEHICLES_ADD} 
        element={
          <PrivateRoute requiredRole="vendor">
            <AddVehicle />
          </PrivateRoute>
        } 
      />
      <Route 
        path={ROUTES.VENDOR_VEHICLES_EDIT} 
        element={
          <PrivateRoute requiredRole="vendor">
            <EditVehicle />
          </PrivateRoute>
        } 
      />
      <Route 
        path={ROUTES.VENDOR_VEHICLES_LIST} 
        element={
          <PrivateRoute requiredRole="vendor">
            <VehicleList />
          </PrivateRoute>
        } 
      />
      <Route 
        path={ROUTES.VENDOR_BOOKINGS} 
        element={
          <PrivateRoute requiredRole="vendor">
            <DashboardLayout>
              <AgencyBookings />
            </DashboardLayout>
          </PrivateRoute>
        } 
      />
      <Route 
        path="/vendor/bookings/:status" 
        element={
          <PrivateRoute requiredRole="vendor">
            <DashboardLayout>
              <AgencyBookings />
            </DashboardLayout>
          </PrivateRoute>
        } 
      />

      {/* 404 - Redirect to home */}
      <Route path="*" element={<Navigate to={ROUTES.HOME} replace />} />
    </Routes>
  );
};

export default AppRoutes;

